import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { ConfirmDeleteModal } from '@/pages/admin/companies/components/ConfirmDeleteModal';
import { EntityGroupCard } from '@/pages/admin/shared/entity-management/EntityGroupCard';
import { EntityManagementSection } from '@/pages/admin/shared/entity-management/EntityManagementSection';
import {
	useCreateFormCategory,
	useCreateFormSubcategory,
	usePatchFormCategory,
	usePatchFormSubcategory,
	useRemoveFormCategory,
	useRemoveFormSubcategory,
} from '@/services/mutations/form-category';
import { useGetFormCategories } from '@/services/queries/form-category';
import {
	getEntityIconStyleForIndex,
	getEntityInitials,
} from '@/pages/admin/shared/entity-management/entityVisuals';
import { CreateCategoryModal } from './components/CreateCategoryModal';
import { CreateSubcategoryModal } from './components/CreateSubcategoryModal';
import { EditCategoryModal } from './components/EditCategoryModal';
import { EditSubcategoryModal } from './components/EditSubcategoryModal';
import { CategoryDetailsModal } from './components/CategoryDetailsModal';

type Category = {
	id: string;
	name: string;
	description?: string | null;
	subcategories: Forms.FormSubcategoryCatalog[];
};

type DeleteTarget =
	| { type: 'category'; categoryId: string }
	| { type: 'subcategory'; categoryId: string; subcategoryId: string };

export const FormCategoriesTab = () => {
	const mapCatalogToCategory = (
		category: Forms.FormCategoryCatalog,
	): Category => ({
		id: category.id,
		name: category.name,
		description: category.description ?? null,
		subcategories: Array.isArray(category.form_subcategory)
			? category.form_subcategory
			: [],
	});

	const [categories, setCategories] = useState<Category[]>([]);
	const { data: categoriesResponse } = useGetFormCategories({
		page: 1,
		limit: 100,
	});
	const { mutate: createCategory } = useCreateFormCategory(
		(createdCategory) => {
			const mapped = mapCatalogToCategory(createdCategory);
			setCategories((prev) => {
				const withoutSame = prev.filter(
					(item) => item.id !== mapped.id,
				);
				return [mapped, ...withoutSame];
			});
		},
	);
	const { mutate: patchCategory } = usePatchFormCategory(
		(updatedCategory) => {
			const mapped = mapCatalogToCategory(updatedCategory);
			setCategories((prev) =>
				prev.map((item) => (item.id === mapped.id ? mapped : item)),
			);
		},
	);
	const { mutateAsync: removeCategory } = useRemoveFormCategory();
	const { mutate: createSubcategory } = useCreateFormSubcategory(
		(createdSubcategory) => {
			setCategories((prev) =>
				prev.map((item) =>
					item.id === createdSubcategory.category_id
						? {
								...item,
								subcategories: [
									...item.subcategories,
									createdSubcategory,
								],
						  }
						: item,
				),
			);
		},
	);
	const { mutate: patchSubcategory } = usePatchFormSubcategory(
		(updatedSubcategory) => {
			setCategories((prev) =>
				prev.map((item) =>
					item.id === updatedSubcategory.category_id
						? {
								...item,
								subcategories: item.subcategories.map((sub) =>
									sub.id === updatedSubcategory.id
										? updatedSubcategory
										: sub,
								),
						  }
						: item,
				),
			);
		},
	);
	const { mutateAsync: removeSubcategory } = useRemoveFormSubcategory();
	const [searchTerm, setSearchTerm] = useState('');
	const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
	const [isSubcategoryModalOpen, setIsSubcategoryModalOpen] = useState(false);
	const [
		subcategoryModalDefaultCategoryId,
		setSubcategoryModalDefaultCategoryId,
	] = useState<string | undefined>(undefined);
	const [editCategoryId, setEditCategoryId] = useState<string | null>(null);
	const [editSubcategory, setEditSubcategory] = useState<{
		categoryId: string;
		subcategoryId: string;
	} | null>(null);
	const [detailsCategoryId, setDetailsCategoryId] = useState<string | null>(
		null,
	);
	const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);

	useEffect(() => {
		if (!categoriesResponse?.data) return;
		setCategories(categoriesResponse.data.map(mapCatalogToCategory));
	}, [categoriesResponse]);

	const deleteModalMeta = useMemo(() => {
		if (!deleteTarget) return null;
		const c = categories.find((x) => x.id === deleteTarget.categoryId);
		if (!c) return null;
		if (deleteTarget.type === 'category') {
			return {
				title: 'Excluir categoria',
				description: (
					<>
						Tem certeza que deseja excluir a categoria{' '}
						<strong className="text-stone-800">{c.name}</strong>?
						Todas as subcategorias serão removidas.
					</>
				),
			};
		}
		const subName = c.subcategories.find(
			(sub) => sub.id === deleteTarget.subcategoryId,
		)?.name;
		if (subName === undefined) return null;
		return {
			title: 'Excluir subcategoria',
			description: (
				<>
					Tem certeza que deseja excluir a subcategoria{' '}
					<strong className="text-stone-800">{subName}</strong> da
					categoria{' '}
					<strong className="text-stone-800">{c.name}</strong>?
				</>
			),
		};
	}, [deleteTarget, categories]);

	useEffect(() => {
		if (deleteTarget && !deleteModalMeta) {
			setDeleteTarget(null);
		}
	}, [deleteTarget, deleteModalMeta]);

	const detailsCategory = useMemo(() => {
		if (!detailsCategoryId) return null;
		return categories.find((c) => c.id === detailsCategoryId) ?? null;
	}, [detailsCategoryId, categories]);

	const detailsCategoryIndex = useMemo(() => {
		if (!detailsCategoryId) return 0;
		const i = categories.findIndex((c) => c.id === detailsCategoryId);
		return i >= 0 ? i : 0;
	}, [detailsCategoryId, categories]);

	useEffect(() => {
		if (!detailsCategoryId) return;
		if (!categories.some((c) => c.id === detailsCategoryId)) {
			setDetailsCategoryId(null);
		}
	}, [categories, detailsCategoryId]);

	const editingCategory = useMemo(() => {
		if (!editCategoryId) return null;
		return categories.find((c) => c.id === editCategoryId) ?? null;
	}, [editCategoryId, categories]);

	const editingSubcategoryContext = useMemo(() => {
		if (!editSubcategory) return null;
		const c = categories.find((x) => x.id === editSubcategory.categoryId);
		if (!c) return null;
		const subcategory = c.subcategories.find(
			(sub) => sub.id === editSubcategory.subcategoryId,
		);
		if (!subcategory) return null;
		return {
			categoryId: c.id,
			subcategoryId: subcategory.id,
			categoryName: c.name,
			initialSubcategoryName: subcategory.name,
		};
	}, [editSubcategory, categories]);

	const categoryOptions = useMemo(
		() => categories.map((c) => ({ id: c.id, name: c.name })),
		[categories],
	);

	const filteredCategories = useMemo(() => {
		const q = searchTerm.toLowerCase().trim();
		if (!q) return categories;
		return categories.filter((c) => {
			if (c.name.toLowerCase().includes(q)) return true;
			return c.subcategories.some((s) =>
				s.name.toLowerCase().includes(q),
			);
		});
	}, [categories, searchTerm]);

	const handleCreateCategory = (payload: {
		name: string;
		description?: string;
	}) => {
		const trimmed = payload.name.trim();
		if (!trimmed) return;
		createCategory({
			name: trimmed,
			...(payload.description?.trim()
				? { description: payload.description.trim() }
				: {}),
		});
	};

	const handleCreateSubcategory = (
		categoryId: string,
		subcategoryName: string,
	) => {
		const trimmed = subcategoryName.trim();
		if (!trimmed) return;
		createSubcategory({
			categoryId,
			body: { name: trimmed },
		});
	};

	const handleUpdateCategory = (
		id: string,
		payload: { name: string; description?: string },
	) => {
		const trimmed = payload.name.trim();
		if (!trimmed) return;
		patchCategory({
			id,
			body: {
				name: trimmed,
				description: payload.description?.trim() || undefined,
			},
		});
		setCategories((prev) =>
			prev.map((c) =>
				c.id === id
					? {
							...c,
							name: trimmed,
							description: payload.description?.trim() || null,
					  }
					: c,
			),
		);
	};

	const handleUpdateSubcategory = (
		categoryId: string,
		subcategoryId: string,
		name: string,
	) => {
		const trimmed = name.trim();
		if (!trimmed) return;
		patchSubcategory({
			categoryId,
			subcategoryId,
			body: { name: trimmed },
		});
		setCategories((prev) =>
			prev.map((c) => {
				if (c.id !== categoryId) return c;
				return {
					...c,
					subcategories: c.subcategories.map((sub) =>
						sub.id === subcategoryId
							? { ...sub, name: trimmed }
							: sub,
					),
				};
			}),
		);
	};

	const handleDeleteSubcategory = async (
		categoryId: string,
		subcategoryId: string,
	) => {
		await removeSubcategory({ categoryId, subcategoryId });
		setEditSubcategory((prev) => {
			if (!prev || prev.categoryId !== categoryId) return prev;
			if (prev.subcategoryId === subcategoryId) return null;
			return prev;
		});
		setCategories((prev) =>
			prev.map((c) => {
				if (c.id !== categoryId) return c;
				return {
					...c,
					subcategories: c.subcategories.filter(
						(sub) => sub.id !== subcategoryId,
					),
				};
			}),
		);
	};

	const cleanupCategoryState = (categoryId: string) => {
		setEditCategoryId((prev) => (prev === categoryId ? null : prev));
		setDetailsCategoryId((prev) => (prev === categoryId ? null : prev));
		setEditSubcategory((prev) =>
			prev?.categoryId === categoryId ? null : prev,
		);
		setCategories((prev) => prev.filter((c) => c.id !== categoryId));
	};

	const handleDeleteCategory = async (categoryId: string) => {
		await removeCategory(categoryId);
		cleanupCategoryState(categoryId);
	};

	const handleConfirmDelete = async () => {
		if (!deleteTarget) return;
		if (deleteTarget.type === 'category') {
			await handleDeleteCategory(deleteTarget.categoryId);
		} else {
			await handleDeleteSubcategory(
				deleteTarget.categoryId,
				deleteTarget.subcategoryId,
			);
		}
		setDeleteTarget(null);
	};

	const openSubcategoryModal = (categoryId?: string) => {
		if (categories.length === 0) {
			toast.info('Crie uma categoria primeiro.');
			return;
		}
		setSubcategoryModalDefaultCategoryId(categoryId);
		setIsSubcategoryModalOpen(true);
	};

	const closeSubcategoryModal = () => {
		setIsSubcategoryModalOpen(false);
		setSubcategoryModalDefaultCategoryId(undefined);
	};

	return (
		<>
			<EntityManagementSection
				className="mt-4 space-y-6"
				title="Categorias"
				description="Organize o formulário por categorias e subcategorias."
				searchPlaceholder="Buscar categoria ou subcategoria..."
				searchValue={searchTerm}
				onSearchChange={setSearchTerm}
				primaryActionText="Nova categoria"
				onPrimaryAction={() => setIsCategoryModalOpen(true)}
				secondaryActionText="Nova subcategoria"
				onSecondaryAction={() => openSubcategoryModal(undefined)}
				hasAnyEntities={categories.length > 0}
				hasVisibleEntities={filteredCategories.length > 0}
				emptyInitialTitle="Nenhuma categoria ainda"
				emptyInitialDescription="Use «Nova categoria» para começar e depois adicione subcategorias."
				emptyActionText="Nova categoria"
				onEmptyActionClick={() => setIsCategoryModalOpen(true)}
			>
				{filteredCategories.map((category, index) => {
					const originalIndex = categories.findIndex(
						(c) => c.id === category.id,
					);
					const cardIndex =
						originalIndex >= 0 ? originalIndex : index;
					return (
						<EntityGroupCard
							key={category.id}
							entityId={category.id}
							entityName={category.name}
							index={cardIndex}
							initials={getEntityInitials(category.name)}
							iconClassName={getEntityIconStyleForIndex(
								cardIndex,
							)}
							childItems={category.subcategories.map(
								(sub) => sub.name,
							)}
							childLabelSingular="SUBCATEGORIA"
							childLabelPlural="SUBCATEGORIAS"
							emptyChildrenTitle="Nenhuma subcategoria ainda"
							emptyChildrenDescription="Adicione uma subcategoria a esta categoria."
							createChildText="Nova subcategoria"
							onEditEntity={() => setEditCategoryId(category.id)}
							onDeleteEntity={() =>
								setDeleteTarget({
									type: 'category',
									categoryId: category.id,
								})
							}
							onEditChild={(subcategoryIndex) =>
								category.subcategories[subcategoryIndex] &&
								setEditSubcategory({
									categoryId: category.id,
									subcategoryId:
										category.subcategories[subcategoryIndex]
											.id,
								})
							}
							onRequestDeleteChild={(subcategoryIndex) =>
								category.subcategories[subcategoryIndex] &&
								setDeleteTarget({
									type: 'subcategory',
									categoryId: category.id,
									subcategoryId:
										category.subcategories[subcategoryIndex]
											.id,
								})
							}
							onViewDetails={() =>
								setDetailsCategoryId(category.id)
							}
							onCreateChild={() =>
								openSubcategoryModal(category.id)
							}
							alwaysShowDetailsButton
						/>
					);
				})}
			</EntityManagementSection>

			<CreateCategoryModal
				isOpen={isCategoryModalOpen}
				onClose={() => setIsCategoryModalOpen(false)}
				onCreate={handleCreateCategory}
			/>

			<CreateSubcategoryModal
				isOpen={isSubcategoryModalOpen}
				onClose={closeSubcategoryModal}
				categories={categoryOptions}
				defaultCategoryId={subcategoryModalDefaultCategoryId}
				onCreate={handleCreateSubcategory}
			/>

			<EditCategoryModal
				isOpen={!!editingCategory}
				onClose={() => setEditCategoryId(null)}
				initialName={editingCategory?.name ?? ''}
				initialDescription={editingCategory?.description ?? ''}
				onSave={(payload) => {
					if (editCategoryId) {
						handleUpdateCategory(editCategoryId, payload);
					}
				}}
			/>

			<EditSubcategoryModal
				isOpen={!!editingSubcategoryContext}
				onClose={() => setEditSubcategory(null)}
				categoryName={editingSubcategoryContext?.categoryName ?? ''}
				initialSubcategoryName={
					editingSubcategoryContext?.initialSubcategoryName ?? ''
				}
				onSave={(name) => {
					if (editingSubcategoryContext) {
						handleUpdateSubcategory(
							editingSubcategoryContext.categoryId,
							editingSubcategoryContext.subcategoryId,
							name,
						);
					}
				}}
			/>

			{detailsCategory && (
				<CategoryDetailsModal
					isOpen
					onClose={() => setDetailsCategoryId(null)}
					categoryName={detailsCategory.name}
					categoryDescription={detailsCategory.description}
					initials={getEntityInitials(detailsCategory.name)}
					iconClassName={getEntityIconStyleForIndex(
						detailsCategoryIndex,
					)}
					subcategories={detailsCategory.subcategories.map(
						(sub) => sub.name,
					)}
					onEditSubcategory={(subcategoryIndex) =>
						detailsCategory.subcategories[subcategoryIndex] &&
						setEditSubcategory({
							categoryId: detailsCategory.id,
							subcategoryId:
								detailsCategory.subcategories[subcategoryIndex]
									.id,
						})
					}
					onDeleteSubcategory={(subcategoryIndex) =>
						detailsCategory.subcategories[subcategoryIndex] &&
						setDeleteTarget({
							type: 'subcategory',
							categoryId: detailsCategory.id,
							subcategoryId:
								detailsCategory.subcategories[subcategoryIndex]
									.id,
						})
					}
					onCreateSubcategory={() =>
						openSubcategoryModal(detailsCategory.id)
					}
				/>
			)}

			<ConfirmDeleteModal
				isOpen={!!deleteModalMeta}
				onClose={() => setDeleteTarget(null)}
				onConfirm={handleConfirmDelete}
				title={deleteModalMeta?.title ?? ''}
				description={deleteModalMeta?.description ?? null}
			/>
		</>
	);
};
